import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';

interface BatchImport {
  id: string;
  display_id: string;
  source_format: 'csv' | 'json' | 'ndjson' | 'md';
  source_filename: string | null;
  posts_total: number;
  posts_imported: number;
  posts_skipped: number;
  status: 'pending' | 'completed' | 'partial' | 'rolled_back';
  created_at: string;
  rollback_until: string;
  rolled_back_at: string | null;
  error_log: Array<{ row: number; message: string }>;
}

interface BatchHistoryProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  onBatchChange?: () => void;
}

interface BatchListResponse {
  success: boolean;
  data: BatchImport[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

const formatIcons: Record<string, string> = {
  csv: '📊',
  json: '📋',
  ndjson: '📄',
  md: '📝',
};

export function BatchHistory({ projectId, isOpen, onClose, onBatchChange }: BatchHistoryProps) {
  const { t } = useTranslation(['posts', 'common']);
  const [batches, setBatches] = useState<BatchImport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [expandedBatch, setExpandedBatch] = useState<string | null>(null);

  const statusLabels: Record<string, { label: string; color: string; icon: string }> = {
    pending: { label: t('posts:batchHistory.status.pending'), color: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
    completed: { label: t('posts:batchHistory.status.completed'), color: 'bg-green-100 text-green-800', icon: '✓' },
    partial: { label: t('posts:batchHistory.status.partial'), color: 'bg-orange-100 text-orange-800', icon: '⚠' },
    rolled_back: { label: t('posts:batchHistory.status.rolledBack'), color: 'bg-gray-100 text-gray-800', icon: '⏪' },
  };

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get<BatchListResponse>(`/batches?project_id=${projectId}`);
      setBatches(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common:error.loading'));
    } finally {
      setLoading(false);
    }
  }, [projectId, t]);

  useEffect(() => {
    if (isOpen) {
      fetchBatches();
    }
  }, [isOpen, fetchBatches]);

  const handleRollback = async (batchId: string) => {
    if (!confirm(t('posts:batchHistory.confirmRollback'))) {
      return;
    }

    setActionLoading(batchId);
    try {
      await api.post(`/batches/${batchId}/rollback`, {});
      await fetchBatches();
      onBatchChange?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('posts:batchHistory.rollbackFailed'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestore = async (batchId: string) => {
    setActionLoading(batchId);
    try {
      await api.post(`/batches/${batchId}/restore`, {});
      await fetchBatches();
      onBatchChange?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('posts:batchHistory.restoreFailed'));
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelativeTime = (isoString: string) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));

    if (diffMs < 0) {
      return t('posts:batchHistory.expired');
    }
    if (diffHours < 1) {
      const diffMinutes = Math.round(diffMs / (1000 * 60));
      return t('posts:batchHistory.minutes', { count: diffMinutes });
    }
    if (diffHours < 24) {
      return t('posts:batchHistory.hours', { count: diffHours });
    }
    const diffDays = Math.round(diffHours / 24);
    return t('posts:batchHistory.days', { count: diffDays });
  };

  const isRollbackExpired = (rollbackUntil: string) => {
    return new Date() > new Date(rollbackUntil);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <span>📦</span> {t('posts:batchHistory.title')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center py-8 text-gray-500">
              {t('posts:batchHistory.loading')}
            </div>
          ) : batches.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-3">📭</div>
              <p>{t('posts:batchHistory.noBatches')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {batches.map((batch) => {
                const statusInfo = statusLabels[batch.status] || statusLabels.pending;
                const canRollback = batch.status !== 'rolled_back' && !isRollbackExpired(batch.rollback_until);
                const canRestore = batch.status === 'rolled_back';
                const isExpanded = expandedBatch === batch.id;
                const isLoading = actionLoading === batch.id;

                return (
                  <div
                    key={batch.id}
                    className={`border rounded-lg overflow-hidden ${
                      batch.status === 'rolled_back' ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'
                    }`}
                  >
                    {/* Main Row */}
                    <div className="p-4 flex items-center gap-4">
                      {/* Format Icon */}
                      <div className="text-2xl">
                        {formatIcons[batch.source_format] || '📄'}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-gray-900">
                            {batch.source_filename || `${t('posts:batchHistory.import')} ${batch.display_id}`}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusInfo.color}`}>
                            {statusInfo.icon} {statusInfo.label}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                          <span>{formatDate(batch.created_at)}</span>
                          <span>
                            {batch.posts_imported}/{batch.posts_total} {t('posts:batchHistory.posts')}
                            {batch.posts_skipped > 0 && (
                              <span className="text-orange-600"> ({t('posts:batchHistory.skipped', { count: batch.posts_skipped })})</span>
                            )}
                          </span>
                          <span className="uppercase text-xs text-gray-400">
                            {batch.source_format}
                          </span>
                        </div>
                      </div>

                      {/* Rollback Timer */}
                      {batch.status !== 'rolled_back' && (
                        <div className="text-right text-xs">
                          {isRollbackExpired(batch.rollback_until) ? (
                            <span className="text-gray-400">{t('posts:batchHistory.rollbackExpired')}</span>
                          ) : (
                            <span className="text-blue-600">
                              {t('posts:batchHistory.rollback')}: {formatRelativeTime(batch.rollback_until)}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {batch.error_log && batch.error_log.length > 0 && (
                          <button
                            onClick={() => setExpandedBatch(isExpanded ? null : batch.id)}
                            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
                            title={t('posts:batchHistory.showErrors')}
                          >
                            {t('posts:batchHistory.errorCount', { count: batch.error_log.length })}
                          </button>
                        )}

                        {canRollback && (
                          <button
                            onClick={() => handleRollback(batch.id)}
                            disabled={isLoading}
                            className="px-3 py-1.5 text-sm bg-red-50 text-red-600 hover:bg-red-100 rounded disabled:opacity-50"
                          >
                            {isLoading ? '...' : `⏪ ${t('posts:batchHistory.rollback')}`}
                          </button>
                        )}

                        {canRestore && (
                          <button
                            onClick={() => handleRestore(batch.id)}
                            disabled={isLoading}
                            className="px-3 py-1.5 text-sm bg-green-50 text-green-600 hover:bg-green-100 rounded disabled:opacity-50"
                          >
                            {isLoading ? '...' : `↩ ${t('posts:batchHistory.restore')}`}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Error Log (expanded) */}
                    {isExpanded && batch.error_log && batch.error_log.length > 0 && (
                      <div className="border-t bg-red-50 p-3">
                        <div className="text-sm font-medium text-red-800 mb-2">
                          {t('posts:batchHistory.errorsOnImport')}:
                        </div>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {batch.error_log.map((err, idx) => (
                            <div key={idx} className="text-xs text-red-700 flex gap-2">
                              <span className="font-mono text-red-500">{t('posts:batchHistory.row', { row: err.row })}:</span>
                              <span>{err.message}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-between items-center">
          <div className="text-xs text-gray-500">
            {t('posts:batchHistory.rollbackPeriod')}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded"
          >
            {t('common:actions.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
